import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Clock, MapPin, Star, Navigation, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { API_URL } from '../config';

const RideShareTripComplete = () => {
  const { tripId, isDriver } = useParams();
  const location = useLocation();
  const [tripData, setTripData] = useState(null);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Parse trip data from URL or location state
  useEffect(() => {
    const getTripData = () => {
      // Try to get trip data from location state first
      if (location.state?.trip) {
        setTripData(location.state.trip);
        setLoading(false);
        return;
      }

      // Try to get from URL parameters
      const urlParams = new URLSearchParams(location.search);
      const tripParam = urlParams.get('trip');
      
      if (tripParam) {
        try {
          const trip = JSON.parse(decodeURIComponent(tripParam));
          setTripData(trip);
        } catch (error) {
          console.error('Error parsing trip data:', error);
        }
      }
      
      setLoading(false);
    };

    getTripData();
  }, [location]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZW', {
      style: 'currency',
      currency: 'ZWL',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "N/A";
    if (minutes < 60) return `${Math.round(minutes)} mins`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const submitRating = async () => {
    if (rating === 0) {
      alert('Please provide a rating before submitting');
      return;
    }

    setSubmitting(true);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      if (isDriver === 'true') {
        // Driver submits feedback about passenger
        const driverFeedbackUrl = `${API_URL}/rideshare/requests/${tripData?.request_id}`;
        const driverPayload = {
          driver_comment_for_customer: feedback,
          driver_rates_customer: rating,
          status: "Completed",
          driver_feedback_date: new Date().toISOString()
        };
        
        const response = await fetch(driverFeedbackUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(driverPayload),
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error("Failed to submit driver feedback");
        }
      } else {
        // Passenger submits feedback about driver
        const customerFeedbackUrl = `${API_URL}/rideshare/requests/${tripData?.request_id}`;
        const customerPayload = {
          customer_comment_for_driver: feedback,
          customer_rates_driver: rating,
          status: "Completed",
          customer_feedback_date: new Date().toISOString()
        };
        
        const response = await fetch(customerFeedbackUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(customerPayload),
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error("Failed to submit passenger feedback");
        }
      }

      alert('Thank you! Your feedback has been submitted successfully.');
      
      // Navigate back to home
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'NAVIGATE_HOME'
        }));
      } else {
        // For browser testing
        window.close();
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      alert("Failed to submit rating. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        className={`star-button ${star <= rating ? 'active' : ''}`}
        onClick={() => setRating(star)}
        style={{
          background: 'none',
          border: 'none',
          fontSize: '28px',
          cursor: 'pointer',
          color: star <= rating ? '#FFC000' : '#ddd',
          padding: '2px',
          transition: 'transform 0.2s, color 0.2s',
        }}
        onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
      >
        ★
      </button>
    ));
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #FFC000',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '10px'
        }}></div>
        <div style={{ fontSize: '16px', color: '#666' }}>Loading trip details...</div>
      </div>
    );
  }

  if (!tripData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
          Trip details not found
        </div>
        <button
          onClick={() => window.history.back()}
          style={{
            backgroundColor: '#FFC000',
            color: '#000',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const finalFare = tripData?.final_fare || 0;
  const tripDistance = tripData?.distance || 0;
  const tripDuration = tripData?.duration || 0;

  return (
    <div style={{
      maxWidth: '100%',
      minHeight: '100vh',
      backgroundColor: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .star-button:hover {
            transform: scale(1.1);
          }
        `}
      </style>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ padding: '20px' }}
      >
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '10px'
          }}>
            <CheckCircle style={{ color: '#28a745', marginRight: '10px' }} size={32} />
            <h1 style={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#28a745',
              margin: 0
            }}>
              Trip Completed!
            </h1>
          </div>
          <p style={{
            fontSize: '14px',
            color: '#666',
            margin: 0
          }}>
            {isDriver === 'true' 
              ? "You have successfully completed this trip!" 
              : "Your trip has been completed successfully!"
            }
          </p>
          <p style={{
            fontSize: '12px',
            color: '#666',
            margin: '5px 0 0 0'
          }}>
            Thank you for using XGOLife Rideshare
          </p>
        </div>

        {/* Success Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          style={{
            backgroundColor: '#d4edda',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #c3e6cb',
            textAlign: 'center'
          }}
        >
          <div style={{
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#155724',
            marginBottom: '5px'
          }}>
            🎉 Trip Successfully Completed
          </div>
          <div style={{
            fontSize: '12px',
            color: '#155724'
          }}>
            Your journey has been recorded and payment processed
          </div>
        </motion.div>

        {/* Trip Summary */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          style={{
            backgroundColor: '#f9f9f9',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid #ddd'
          }}
        >
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '15px',
            color: '#333',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Navigation size={18} style={{ marginRight: '8px', color: '#FFC000' }} />
            Trip Summary
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '14px', color: '#666', flex: 1 }}>
                {isDriver === 'true' ? "Total Earned:" : "Fare Paid:"}
              </span>
              <span style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: '#FFC000',
                flex: 2,
                textAlign: 'right'
              }}>
                {formatCurrency(finalFare)}
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', color: '#666' }}>
                <Clock size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Duration:
              </span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                {formatDuration(tripDuration)}
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', color: '#666' }}>
                <MapPin size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Distance:
              </span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                {tripDistance.toFixed(1)} km
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '14px', color: '#666', flex: 1 }}>
                From:
              </span>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 'bold', 
                color: '#333',
                flex: 2,
                textAlign: 'right'
              }}>
                {isDriver === 'true' 
                  ? (tripData?.origin_name || `${tripData?.origin_lat?.toFixed(6)}, ${tripData?.origin_lng?.toFixed(6)}`) 
                  : (tripData?.pickup_name || `${tripData?.pickup_lat?.toFixed(6)}, ${tripData?.pickup_lng?.toFixed(6)}`)
                }
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '14px', color: '#666', flex: 1 }}>
                To:
              </span>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 'bold', 
                color: '#333',
                flex: 2,
                textAlign: 'right'
              }}>
                {isDriver === 'true' 
                  ? (tripData?.destination_name || `${tripData?.destination_lat?.toFixed(6)}, ${tripData?.destination_lng?.toFixed(6)}`) 
                  : (tripData?.dropoff_name || `${tripData?.dropoff_lat?.toFixed(6)}, ${tripData?.dropoff_lng?.toFixed(6)}`)
                }
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', color: '#666' }}>
                Trip ID:
              </span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                {tripData?.rideshare_id || tripData?.request_id || 'N/A'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Rating Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
          style={{
            backgroundColor: '#f9f9f9',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid #ddd'
          }}
        >
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '15px',
            color: '#333',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Star size={18} style={{ marginRight: '8px', color: '#FFC000' }} />
            Rate Your Experience
          </h2>
          
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '15px',
            textAlign: 'center'
          }}>
            How was your {isDriver === 'true' ? "passenger" : "driver"}?
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '15px',
            gap: '2px'
          }}>
            {renderStars()}
          </div>

          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Share your experience (optional)"
            rows={3}
            style={{
              width: '100%',
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '10px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              backgroundColor: '#fff'
            }}
          />
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          <button
            onClick={submitRating}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
              cursor: submitting ? 'not-allowed' : 'pointer',
              backgroundColor: submitting ? '#ccc' : '#28a745',
              color: '#fff',
              transition: 'opacity 0.2s',
              opacity: submitting ? 0.6 : 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {submitting ? "Submitting..." : "Submit Rating"}
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default RideShareTripComplete;
